<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import EditScopeSelector from '@/components/calculation-result/EditScopeSelector.vue'
import LabelRenamePanel from '@/components/calculation-result/LabelRenamePanel.vue'
import SourceRowEditor from '@/components/calculation-result/SourceRowEditor.vue'
import { useQuantityBuildingBlockStore } from '@/stores/quantityBuildingBlock'
import { useChartConfigurationStore } from '@/stores/chartConfiguration'
import type { QuantityBuildingBlock } from '@/stores/type/quantityBuildingBlock.type'
import type {
  ChartConfiguration,
  RowOverrideByQuantityBlock
} from '@/stores/type/chartConfiguration.type'
import { normalizeLabels } from '@/views/calculations/utils/quantityLabels'
import { EDIT_SCOPES, type EditScope } from '@/constants/editScope'

const props = withDefaults(
  defineProps<{
    modelValue: boolean
    calculationId: number
    readonly?: boolean
    chartConfiguration?: ChartConfiguration | null
  }>(),
  { readonly: false, chartConfiguration: null }
)
const emit = defineEmits<{
  (event: 'update:modelValue', value: boolean): void
  (event: 'saved', payload?: { renames?: { from: string; to: string }[]; scope?: EditScope }): void
}>()

type DraftRow = {
  original: QuantityBuildingBlock
  alias: string
  labels: string[]
  baselineAlias: string
  baselineLabels: string[]
}

const quantityBuildingBlockStore = useQuantityBuildingBlockStore()
const chartConfigurationStore = useChartConfigurationStore()

const draft = ref<DraftRow[]>([])
const loading = ref(false)
const saving = ref(false)
const scope = ref<EditScope>(EDIT_SCOPES.ALL)

const loadRows = async () => {
  loading.value = true
  try {
    const rows = await quantityBuildingBlockStore.fetchItems({ calculation: props.calculationId })
    const list = Array.isArray(rows) ? rows : []
    const overrides: RowOverrideByQuantityBlock = {}
    for (const override of props.chartConfiguration?.row_overrides ?? []) {
      overrides[override.quantity_building_block] = override
    }
    draft.value = list.map((row) => {
      const override = overrides[row.id]
      const alias = override?.display_name ?? row.alias ?? ''
      const labels = normalizeLabels(override?.labels ?? row.labels ?? [])
      return {
        original: row,
        alias,
        labels,
        baselineAlias: alias,
        baselineLabels: labels
      }
    })
  } finally {
    loading.value = false
  }
}

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      scope.value = EDIT_SCOPES.ALL
      loadRows()
    }
  }
)

const labelSuggestions = computed(() => {
  const values = new Set(draft.value.flatMap((rowDraft) => rowDraft.labels))
  return Array.from(values).sort((a, b) => a.localeCompare(b))
})

const applyLocalRename = (from: string, to: string) => {
  const replace = (list: string[]) =>
    normalizeLabels(list.map((label) => (label === from ? to : label)))
  for (const rowDraft of draft.value) {
    rowDraft.labels = replace(rowDraft.labels)
    rowDraft.baselineLabels = replace(rowDraft.baselineLabels)
    rowDraft.original = { ...rowDraft.original, labels: replace(rowDraft.original.labels ?? []) }
  }
}

const onLabelRenamed = ({ from, to }: { from: string; to: string }) => {
  applyLocalRename(from, to)
  emit('saved')
}

const effectiveName = (rowDraft: DraftRow) => rowDraft.alias.trim() || rowDraft.original.name

const duplicateNames = computed(() => {
  const counts = new Map<string, number>()
  for (const rowDraft of draft.value) {
    const name = effectiveName(rowDraft)
    if (name) counts.set(name, (counts.get(name) ?? 0) + 1)
  }
  return new Set(
    [...counts]
      .filter(
        ([name, count]) =>
          count > 1 && draft.value.some((rowDraft) => rowDraft.alias.trim() === name)
      )
      .map(([name]) => name)
  )
})

const hasDuplicates = computed(() => duplicateNames.value.size > 0)

const close = () => emit('update:modelValue', false)

const save = async () => {
  if (props.readonly || hasDuplicates.value) return
  saving.value = true
  try {
    const renames: { from: string; to: string }[] = []
    for (const rowDraft of draft.value) {
      const from = rowDraft.baselineAlias.trim() || rowDraft.original.name
      const to = effectiveName(rowDraft)
      if (from && to && from !== to) renames.push({ from, to })
    }

    if (scope.value === EDIT_SCOPES.ALL) {
      await Promise.all(
        draft.value.map((rowDraft) =>
          quantityBuildingBlockStore.update({
            ...rowDraft.original,
            alias: rowDraft.alias.trim() || null,
            labels: normalizeLabels(rowDraft.labels)
          })
        )
      )
      emit('saved', { renames, scope: EDIT_SCOPES.ALL })
    } else if (props.chartConfiguration) {
      const overrides = draft.value
        .filter((rowDraft) => rowDraft.alias.trim() || rowDraft.labels.length)
        .map((rowDraft) => ({
          quantity_building_block: rowDraft.original.id,
          display_name: rowDraft.alias.trim() || null,
          labels: rowDraft.labels.length ? normalizeLabels(rowDraft.labels) : null
        }))
      await chartConfigurationStore.updateConfiguration({
        ...props.chartConfiguration,
        row_overrides: overrides
      })
      emit('saved', { renames, scope: EDIT_SCOPES.CHART })
    }
    close()
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <v-dialog :model-value="props.modelValue" max-width="720" @update:model-value="close">
    <v-card>
      <v-card-title class="d-flex align-center">
        Modify Label
        <v-spacer />
        <v-icon @click="close">mdi-close</v-icon>
      </v-card-title>

      <v-card-text>
        <div v-if="loading" class="py-6 text-center">
          <v-progress-circular indeterminate size="28" />
        </div>

        <template v-else>
          <EditScopeSelector v-model="scope" :chart-available="!!props.chartConfiguration" />

          <v-divider class="mb-4" />

          <LabelRenamePanel
            :calculation-id="props.calculationId"
            :suggestions="labelSuggestions"
            :readonly="props.readonly"
            @renamed="onLabelRenamed"
          />

          <v-divider class="mb-4" />

          <div class="d-flex flex-column ga-4">
            <v-alert v-if="hasDuplicates" type="error" variant="tonal">
              Display names must be unique in
              {{ scope === EDIT_SCOPES.ALL ? 'this calculation' : 'this chart' }}:
              {{ [...duplicateNames].join(', ') }}
            </v-alert>
            <SourceRowEditor
              v-for="rowDraft in draft"
              :key="rowDraft.original.id"
              v-model:alias="rowDraft.alias"
              v-model:labels="rowDraft.labels"
              :name="rowDraft.original.name"
              :suggestions="labelSuggestions"
              :readonly="props.readonly"
              :duplicate="duplicateNames.has(effectiveName(rowDraft))"
            />
          </div>
        </template>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="close">Cancel</v-btn>
        <v-btn
          color="primary"
          :loading="saving"
          :disabled="props.readonly || hasDuplicates"
          @click="save"
          >Save</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
